'use client'

import { useState } from 'react'
import {
  UserCog,
  CheckSquare,
  Calendar,
  MailCheck,
  Mic,
  Sparkles,
  MessageCircle,
  Search,
} from 'lucide-react'
import FeatureDetailModal from '@/components/landing/feature-detail-modal'

const features = [
  {
    title: 'Executive Assistant',
    sub: 'Voice AI',
    plats: 'iOS · Android · Web · Chrome',
    blurb: 'Talk to AVA — your personal AI assistant that gets things done for you.',
    Anim: MicAnim,
    icon: UserCog,
    color: 'from-violet-500 to-purple-500',
    detailedDescription: "Your personal AI executive assistant that understands you, remembers your preferences, and gets things done just by having a natural conversation. Talk to AVA the way you'd talk to a real assistant — ask, delegate, or discuss, and AVA will plan, organize, create notes, schedule meetings, and follow up on what matters most.",
    platforms: ['iOS', 'Android', 'Web', 'Chrome Extension'],
    benefits: [
      'Interacts naturally — no commands, just conversation',
      'Understands context and adapts to how you work',
      'Handles planning, scheduling, and reminders automatically',
      'Seamlessly organizes your day across all devices',
      'Connects with every AVA feature for effortless productivity',
    ],
    screenshot: '/features/executive_assistant.gif',
  },
  {
    title: 'Smart Planner',
    sub: 'Tasks',
    plats: 'iOS · Android · Web · Chrome',
    blurb: 'Never miss a task — plan, organize, and track your work & life by voice or text.',
    Anim: PlannerAnim,
    icon: CheckSquare,
    color: 'from-green-500 to-emerald-500',
    detailedDescription: 'Your intelligent planner that keeps everything organized and easy to manage. AVA brings all your personal and work tasks into one clean timeline—Today, Tomorrow, This Week, and This Month—so you can focus, prioritize, and stay in control.',
    platforms: ['iOS', 'Android', 'Web', 'Chrome Extension'],
    benefits: [
      'Unified planner for personal and professional goals',
      'Auto-prioritized tasks by importance and timeline',
      'Create, update, or delegate tasks naturally—by voice or text',
      'Recurring tasks and smart reminders built in',
      'Organize and filter tasks by projects for a clear overview',
    ],
    screenshot: '/features/smart_planner_v2.gif',
  },
  {
    title: 'Calendar Hub',
    sub: 'Schedule',
    plats: 'iOS · Android · Web · Chrome',
    blurb: 'Connect Google, Outlook, and iOS calendars to plan your day smarter.',
    Anim: CalendarAnim,
    icon: Calendar,
    color: 'from-blue-500 to-cyan-500',
    detailedDescription: 'Unify all your calendars in one intelligent view. See personal and work schedules together, plan your week visually, and manage events hands-free using natural voice commands.',
    platforms: ['iOS', 'Android', 'Web', 'Chrome Extension'],
    benefits: [
      'Combines Google, Outlook, and iOS calendars seamlessly',
      'Smart conflict detection and auto-rescheduling',
      'Create or adjust events naturally—by voice or text',
      'Clear daily, weekly, and monthly visualization',
      'Connected with tasks and briefs for full-day context',
    ],
    screenshot: '/features/calendar_integration.gif',
  },
  {
    title: 'Zero Inbox',
    sub: 'Email',
    plats: 'iOS · Android',
    blurb: 'Automatically sort emails and turn important ones into actionable tasks.',
    Anim: InboxAnim,
    icon: MailCheck,
    color: 'from-blue-500 to-cyan-500',
    detailedDescription: 'Unify and declutter your emails with an intelligent inbox that works for you. AVA categorizes incoming emails into Action, FYI, Notifications, or Promotions, automatically surfacing what matters most and converting key messages into tasks for follow-up.',
    platforms: ['iOS', 'Android'],
    benefits: [
      '**Never miss actions** — turns deadlines and requests into tasks',
      '**Cuts clutter** — filters low-value emails automatically',
      '**Saves time** — manage your inbox in minutes',
      '**Stays organized** — tasks and emails in one workspace',
      '**Keeps you calm** — clean, distraction-free inbox always',
    ],
    screenshot: '/features/zeroinbox.gif',
  },
  {
    title: 'Voice Notes',
    sub: 'Record',
    plats: 'iOS · Android',
    blurb: 'Record meetings, lectures, or ideas — get structured summaries with key takeaways.',
    Anim: VoiceNotesAnim,
    icon: Mic,
    color: 'from-indigo-500 to-blue-500',
    detailedDescription: 'Capture every idea, meeting, or lecture — and let AVA turn your voice into organized, actionable notes. With four smart modes—Meeting, Lecture, Journal, and Dictation—AVA records, transcribes, and summarizes automatically so nothing important ever gets lost.',
    platforms: ['iOS', 'Android'],
    benefits: [
      '**Never miss a detail** — AVA captures everything while you stay present',
      '**Save time** — instant summaries, no manual note-taking',
      '**Stay organized** — structured notes, always easy to find',
      '**Think freely** — speak naturally, AVA does the rest',
      '**Review faster** — jump to highlights anytime',
    ],
    screenshot: '/features/voicenote.gif',
  },
  {
    title: 'AI Tasks & Research',
    sub: 'Automate',
    plats: 'iOS · Android · Web',
    blurb: 'Ask AVA to research, draft, or generate content — even on a recurring schedule.',
    Anim: ResearchAnim,
    icon: Sparkles,
    color: 'from-violet-500 to-purple-500',
    detailedDescription: 'Your personal AI analyst that researches, learns, and reports for you. From tracking technology trends and financial markets to creating summaries or insights on any topic — just ask AVA. It searches the web, analyzes information, and delivers concise, easy-to-understand updates tailored to your work or interests.',
    platforms: ['iOS', 'Android', 'Web'],
    benefits: [
      '**Gets the latest insights** on technology, finance, or any topic',
      '**Researches and summarizes** from reliable online sources',
      '**Schedules recurring reports** and daily news briefs automatically',
      '**Saves hours every week** by keeping you informed effortlessly',
    ],
    screenshot: '/features/ai_task.gif',
  },
  {
    title: 'Chat with AVA on WhatsApp',
    sub: 'Anywhere',
    plats: 'WhatsApp',
    blurb: 'Create tasks, notes, and get your daily brief — right from WhatsApp.',
    Anim: WhatsappAnim,
    icon: MessageCircle,
    color: 'from-green-600 to-emerald-600',
    detailedDescription: 'Turn WhatsApp into your personal productivity channel. Chat with AVA to create tasks, take notes, and get updates — all without leaving your conversations. Forward messages, ask for summaries, or receive your daily briefs directly in chat, synced instantly with your AVA account.',
    platforms: ['WhatsApp'],
    benefits: [
      '**Stay productive right from WhatsApp** — no app switching needed',
      '**Turn forwarded messages** into instant tasks or notes',
      '**Receive morning and evening briefs** directly in chat',
      '**Syncs seamlessly** with your app and web workspace',
      '**Perfect for quick actions** and productivity on the go',
    ],
    screenshot: '/features/chat_with_whatsapp_optimized.gif',
  },
  {
    title: 'Smart Search',
    sub: 'Find',
    plats: 'iOS · Android · Web',
    blurb: 'Search by context, not exact words — find notes, PDFs, images and more.',
    Anim: SearchAnim,
    icon: Search,
    color: 'from-indigo-500 to-purple-500',
    detailedDescription: "Ask AVA anything, just like you would your personal assistant. AVA understands context — not just keywords — so you can search naturally and get meaningful answers. Whether it's a note, document, image, PDF, or attachment, AVA finds exactly what you need and explains it in context.",
    platforms: ['iOS', 'Android', 'Web'],
    benefits: [
      '**Understands questions like a real assistant** — not keyword searches',
      '**Searches across notes, PDFs, images, and attachments** instantly',
      '**Gives context-based answers**, not just results',
      '**Saves time** with quick, accurate responses',
      '**Keeps all searches private and secure**',
    ],
    screenshot: '/features/smart_search.gif',
  },
]

export default function FeaturesGrid() {
  const [selected, setSelected] = useState(null)
  return (
    <>
      <div className="features-grid">
        {features.map((f) => (
          <FeatureCard key={f.title} feature={f} onClick={() => setSelected(f)}>
            {(hover) => <f.Anim hover={hover} />}
          </FeatureCard>
        ))}
      </div>
      <FeatureDetailModal
        feature={selected}
        isOpen={!!selected}
        onClose={() => setSelected(null)}
      />
    </>
  )
}

function FeatureCard({ feature, onClick, children }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      className="feature-card"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        transform: hover ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hover
          ? '0 24px 50px rgba(139, 92, 246, 0.18), 0 2px 6px rgba(20,14,50,0.04)'
          : '0 2px 6px rgba(20,14,50,0.03)',
      }}
    >
      <div className="feature-anim">
        {children(hover)}
      </div>
      <div className="feature-sub">{feature.sub}</div>
      <h3 className="feature-title">{feature.title}</h3>
      {feature.blurb && <p className="feature-blurb">{feature.blurb}</p>}
      <div className="feature-plats">{feature.plats}</div>
      <span className="feature-hint">
        Click for details
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
          <path d="M5 4h7v7M12 4L4 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </button>
  )
}

/* ---------- Animations (violet single-accent) ---------- */

// 1. Mic with ripples
function MicAnim({ hover }) {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          position: 'absolute',
          width: 60, height: 60, borderRadius: '50%',
          border: '2px solid var(--accent)',
          opacity: 0,
          animation: hover ? `avaRipple 1.6s ${i * 0.5}s ease-out infinite` : 'none',
        }} />
      ))}
      <div style={{
        width: 64, height: 64, borderRadius: 18,
        background: 'linear-gradient(135deg, var(--accent), var(--accent-700))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 14px 30px rgba(139, 92, 246, 0.4)',
        transform: hover ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 400ms',
      }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <rect x="9" y="3" width="6" height="12" rx="3" fill="#fff" />
          <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  )
}

// 2. Planner — tasks check off
function PlannerAnim({ hover }) {
  const tasks = ['Draft proposal', 'Review PR', 'Gym @ 6']
  return (
    <div style={{ position: 'absolute', inset: 20, display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center' }}>
      {tasks.map((t, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 14px', background: '#fff', borderRadius: 10,
          border: '1px solid var(--line)',
          transform: hover ? `translateX(${i * 4}px)` : 'translateX(0)',
          transition: `all 500ms cubic-bezier(0.2,0.8,0.2,1) ${i * 80}ms`,
        }}>
          <div style={{
            width: 18, height: 18, borderRadius: 6,
            border: '2px solid var(--accent)',
            background: hover ? 'var(--accent)' : 'transparent',
            transition: `all 300ms ${i * 120 + 200}ms`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {hover && (
              <svg width="10" height="10" viewBox="0 0 10 10">
                <path d="M1 5l3 3 5-6" stroke="#fff" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" fill="none"
                  style={{ strokeDasharray: 12, strokeDashoffset: 12, animation: `avaDrawCheck 300ms ${i * 120 + 300}ms forwards` }}
                />
              </svg>
            )}
          </div>
          <div style={{
            fontSize: 13, fontWeight: 500, color: 'var(--ink)',
            textDecoration: hover ? 'line-through' : 'none',
            opacity: hover ? 0.5 : 1,
            transition: `all 300ms ${i * 120 + 300}ms`,
          }}>{t}</div>
        </div>
      ))}
    </div>
  )
}

// 3. Calendar — violet cells highlight
function CalendarAnim({ hover }) {
  const cells = Array.from({ length: 21 })
  return (
    <div style={{ position: 'absolute', inset: 24, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 5 }}>
      {cells.map((_, i) => {
        const active = [3, 5, 10, 12, 17].includes(i)
        return (
          <div key={i} style={{
            aspectRatio: '1',
            borderRadius: 6,
            background: active ? 'linear-gradient(135deg, var(--accent), var(--accent-700))' : '#fff',
            border: active ? 'none' : '1px solid var(--line)',
            boxShadow: active ? '0 4px 12px rgba(139, 92, 246, 0.35)' : 'none',
            transform: hover && active ? 'scale(1.15) translateY(-3px)' : 'scale(1)',
            transition: `all 400ms cubic-bezier(0.2,0.8,0.2,1) ${i * 25}ms`,
          }} />
        )
      })}
    </div>
  )
}

// 4. Inbox → envelopes fly into folder
function InboxAnim({ hover }) {
  const items = [{ tag: 'Task' }, { tag: 'FYI' }, { tag: 'Reply' }]
  return (
    <div style={{ position: 'absolute', inset: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((_, i) => (
          <div key={i} style={{
            height: 22, borderRadius: 6, background: '#fff',
            border: '1px solid var(--line)',
            display: 'flex', alignItems: 'center', padding: '0 8px', gap: 6,
            transform: hover ? `translateX(${56 + i * 10}px)` : 'translateX(0)',
            opacity: hover ? 0 : 1,
            transition: `all 700ms cubic-bezier(0.7,0,0.3,1) ${i * 120}ms`,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--accent)' }} />
            <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--accent-100)' }} />
          </div>
        ))}
      </div>
      <div style={{
        width: 60, height: 56,
        background: 'linear-gradient(135deg, var(--accent), var(--accent-700))',
        borderRadius: 10,
        position: 'relative',
        transform: hover ? 'scale(1.08)' : 'scale(1)',
        transition: 'all 400ms',
        boxShadow: hover ? '0 12px 24px rgba(139, 92, 246, 0.4)' : '0 4px 12px rgba(139, 92, 246, 0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1 }}>0</div>
      </div>
    </div>
  )
}

// 5. Voice waveform
function VoiceNotesAnim({ hover }) {
  const bars = Array.from({ length: 28 })
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
      {bars.map((_, i) => {
        const height = 10 + Math.abs(Math.sin(i * 0.6)) * 50
        return (
          <div key={i} style={{
            width: 4,
            height: hover ? height : 10,
            borderRadius: 3,
            background: 'linear-gradient(180deg, var(--accent), var(--accent-700))',
            transition: `all 500ms cubic-bezier(0.2,0.8,0.2,1) ${i * 18}ms`,
            animation: hover ? `avaPulseBar 1.2s ${i * 0.05}s ease-in-out infinite` : 'none',
          }} />
        )
      })}
    </div>
  )
}

// 6. Research particles orbiting
function ResearchAnim({ hover }) {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        width: 64, height: 64, borderRadius: 18,
        background: 'linear-gradient(135deg, var(--accent), var(--accent-700))',
        boxShadow: '0 14px 30px rgba(139, 92, 246, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 26, color: '#fff', fontWeight: 600,
      }}>
        ✦
      </div>
      {[0, 1, 2, 3, 4, 5].map(i => {
        const angle = (i / 6) * 360
        const radius = hover ? 64 : 50
        const violet = ['#A78BFA', '#8B5CF6', '#7C3AED', '#C4B5FD', '#6D28D9', '#DDD6FE'][i]
        return (
          <div key={i} style={{
            position: 'absolute',
            width: 12, height: 12, borderRadius: '50%',
            background: violet,
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
            transform: `translate(${Math.cos(angle * Math.PI / 180) * radius}px, ${Math.sin(angle * Math.PI / 180) * radius}px)`,
            transition: `all 700ms cubic-bezier(0.2,0.8,0.2,1) ${i * 50}ms`,
          }} />
        )
      })}
    </div>
  )
}

// 7. WhatsApp chat bubbles
function WhatsappAnim({ hover }) {
  const bubbles = [
    { side: 'l', w: '60%', tone: '#fff' },
    { side: 'r', w: '50%', tone: 'var(--accent-100)' },
    { side: 'l', w: '70%', tone: '#fff' },
  ]
  return (
    <div style={{ position: 'absolute', inset: 20, display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
      {bubbles.map((b, i) => (
        <div key={i} style={{
          alignSelf: b.side === 'l' ? 'flex-start' : 'flex-end',
          width: b.w, height: 28,
          background: b.tone,
          border: b.tone === '#fff' ? '1px solid var(--line)' : '1px solid var(--accent-200)',
          borderRadius: b.side === 'l' ? '14px 14px 14px 4px' : '14px 14px 4px 14px',
          transform: hover ? 'translateX(0) scale(1)' : `translateX(${b.side === 'l' ? -20 : 20}px) scale(0.9)`,
          opacity: hover ? 1 : 0.6,
          transition: `all 500ms cubic-bezier(0.2,0.8,0.2,1) ${i * 120}ms`,
        }} />
      ))}
    </div>
  )
}

// 8. Smart Search — magnifier + result dots
function SearchAnim({ hover }) {
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {[0, 1, 2, 3, 4].map(i => {
        const angle = (i / 5) * 360
        const restRadius = hover ? 14 : 70
        const x = Math.cos(angle * Math.PI / 180) * restRadius
        const y = Math.sin(angle * Math.PI / 180) * restRadius
        return (
          <div key={i} style={{
            position: 'absolute',
            width: 10, height: 10, borderRadius: '50%',
            background: 'var(--accent)',
            opacity: hover ? 0 : 0.5,
            transform: `translate(${x}px, ${y}px)`,
            transition: `all 700ms cubic-bezier(0.2,0.8,0.2,1) ${i * 90}ms`,
          }} />
        )
      })}
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        border: '2.5px solid var(--accent)',
        position: 'relative',
        transform: hover ? 'scale(1.1)' : 'scale(1)',
        transition: 'transform 400ms',
        background: 'rgba(139, 92, 246, 0.06)',
      }}>
        <div style={{
          position: 'absolute',
          right: -12, bottom: -12,
          width: 22, height: 4,
          borderRadius: 2,
          background: 'var(--accent-700)',
          transform: 'rotate(45deg)',
          transformOrigin: 'left center',
        }} />
      </div>
    </div>
  )
}
